import { useEffect, useState } from 'react';
import { Alert, Checkbox, Space, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { DappIdentity, DappVerification } from '@/lib/walletconnect/verify';

const { Text } = Typography;

const TAG_COLOR: Record<DappVerification, string> = {
  verified: 'green',
  mismatch: 'red',
  unverified: 'orange',
  scam: 'red',
};

interface DappIdentityNoticeProps {
  identity: DappIdentity;
  /** For a domain mismatch the user must tick an acknowledgement first. */
  onAcknowledgedChange?: (acknowledged: boolean) => void;
}

/**
 * "Who is asking" for a WalletConnect request, from the Verify-attested origin
 * (ENTX-A-4). The dApp's self-declared name is shown only as a secondary,
 * explicitly-claimed line.
 */
export function DappIdentityNotice({ identity, onAcknowledgedChange }: DappIdentityNoticeProps) {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);
  const origin = identity.origin ?? t('walletconnect.verify.unknown_origin');

  useEffect(() => {
    setAcknowledged(false);
    onAcknowledgedChange?.(false);
    // Reset whenever a different request is displayed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity.origin, identity.verification]);

  const description =
    identity.verification === 'verified'
      ? t('walletconnect.verify.verified_desc', { origin })
      : identity.verification === 'mismatch'
        ? t('walletconnect.verify.mismatch_desc', {
            origin,
            claimed: identity.claimedOrigin ?? (identity.claimedName || '—'),
          })
        : identity.verification === 'scam'
          ? t('walletconnect.verify.scam_desc', { origin })
          : t('walletconnect.verify.unverified_desc', { origin });

  return (
    <Space orientation="vertical" size={4} style={{ width: '100%' }} data-testid="dapp-identity">
      <div>
        <Text type="secondary">{t('walletconnect.verify.requested_by')}: </Text>
        <Text strong className="break-all" data-testid="dapp-identity-origin">
          {origin}
        </Text>{' '}
        <Tag color={TAG_COLOR[identity.verification]} data-testid="dapp-identity-status">
          {t(`walletconnect.verify.${identity.verification}`)}
        </Tag>
      </div>
      {identity.claimedName && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('walletconnect.verify.claims', {
            name: identity.claimedName,
            url: identity.claimedOrigin ?? '—',
          })}
        </Text>
      )}
      {identity.verification !== 'verified' && (
        <Alert
          type={identity.verification === 'unverified' ? 'warning' : 'error'}
          showIcon
          title={description}
        />
      )}
      {identity.verification === 'mismatch' && (
        <Checkbox
          checked={acknowledged}
          onChange={(e) => {
            setAcknowledged(e.target.checked);
            onAcknowledgedChange?.(e.target.checked);
          }}
          data-testid="dapp-identity-ack"
        >
          {t('walletconnect.verify.acknowledge_mismatch')}
        </Checkbox>
      )}
    </Space>
  );
}
